import type { LimitSnapshot, ResolvedLimit } from "./types";
import { ROLLING_WEEK_MS } from "./windows";

export type { ResolvedLimit };

/**
 * Bring a stored snapshot up to date with elapsed time.
 *
 * The premise this rests on: **a machine that is powered off cannot consume
 * quota.** So when a window's reset has passed and nothing has reported since,
 * "used = 0" is not a guess — it follows, provided the account is only reachable
 * from that machine. (Claude Code on mobile is a remote view of a session on the
 * PC, so it does not break this; a browser session on the vendor's website
 * would, and nothing here can see that.)
 *
 * Windows differ in whether a *next* reset can be named, and conflating them
 * would invent data:
 *
 * - `5h` is usage-anchored — the window opens when you first use the tool, so
 *   after it lapses there is no active window and no next reset to report.
 * - `weekly` runs on a fixed 7-day cadence, so the next boundary is arithmetic.
 * - `monthly` follows the billing boundary, so it advances by calendar month.
 */
export function resolveLimit(snap: LimitSnapshot, now: number): ResolvedLimit {
  // No reset time means no way to reason about elapsed time — report as stored.
  if (snap.resetsAt === null || snap.resetsAt > now) {
    return { ...snap, inferred: false };
  }
  return {
    ...snap,
    used: 0,
    resetsAt: nextReset(snap, now),
    inferred: true,
  };
}

/** The next boundary after `now`, or null when the window has no fixed cadence. */
function nextReset(snap: LimitSnapshot, now: number): number | null {
  const from = snap.resetsAt;
  if (from === null) return null;

  switch (snap.window) {
    case "5h":
      // Usage-anchored: no window is open until the tool is next used.
      return null;
    case "weekly": {
      const elapsed = now - from;
      const periods = Math.floor(elapsed / ROLLING_WEEK_MS) + 1;
      return from + periods * ROLLING_WEEK_MS;
    }
    case "monthly":
      return nextMonthlyBoundary(from, now);
  }
}

/**
 * Advance a monthly boundary past `now`, preserving the day-of-month anchor.
 *
 * Calendar months are uneven, so this steps rather than multiplies. Each step is
 * recomputed from the original anchor rather than mutating one date forward:
 * `setMonth` alone overflows on long-day anchors — Jan 31 + 1 month is Feb 31,
 * which normalizes to Mar 3 — and because the overflowed date is then the base
 * for the next step, the anchor is corrupted permanently, not just once. Short
 * months clamp to their last day instead, which is how billing dates behave.
 *
 * Stepping is done in UTC: these timestamps arrive as UTC instants from vendor
 * APIs, and local-time arithmetic would shift the projected boundary by an hour
 * across a DST change for no reason.
 *
 * The cap exists because a corrupt far-past timestamp would otherwise spin: a
 * reading that stale is not worth projecting from, so we decline to name a reset.
 */
const MAX_MONTH_STEPS = 120;
function nextMonthlyBoundary(from: number, now: number): number | null {
  const src = new Date(from);
  const anchorDay = src.getUTCDate();
  for (let i = 1; i <= MAX_MONTH_STEPS; i++) {
    const d = new Date(from);
    // Park on the 1st before changing month so the month shift can't overflow,
    // then place the anchor day, clamped to the target month's length.
    d.setUTCDate(1);
    d.setUTCMonth(src.getUTCMonth() + i);
    d.setUTCDate(Math.min(anchorDay, daysInUtcMonth(d.getUTCFullYear(), d.getUTCMonth())));
    if (d.getTime() > now) return d.getTime();
  }
  return null;
}

/** Day count of a UTC month; day 0 of the next month is the last of this one. */
function daysInUtcMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}
